import { z } from 'zod';
import { id, isoTimestamp } from '@quizee/events';
import { CONSENT_STORED_MAX } from './consent';
import { BRAND, RADIUS } from './tokens';

/**
 * The interaction engine's content model.
 *
 * There is one content shape, not one per format. A poll, a trivia quiz and a survey are all
 * ordered questions with options and a presentation; what differs is the constraints each format
 * puts on that shape (see formats.ts) and how the renderer interprets it. If a change here only
 * makes sense for one format, it belongs in the format constraints instead.
 */

export const FORMATS = ['poll', 'trivia_quiz', 'personality_quiz', 'survey', 'form'] as const;
export type Format = (typeof FORMATS)[number];

export const QUESTION_TYPES = ['single_choice', 'multiple_choice', 'free_text', 'scale'] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

export const optionSchema = z
  .object({
    id: id('option'),
    label: z.string().min(1).max(300),
    image_url: z.string().url().max(2048).optional(),
    /** Trivia only. Which option or options score a point. */
    is_correct: z.boolean().optional(),
    /**
     * Personality only. How much choosing this option pushes the reader towards each outcome,
     * keyed by outcome id.
     *
     * Positive and bounded on purpose. A zero weight is indistinguishable from not pointing at the
     * outcome at all, and a negative one asks the renderer to subtract, which no result screen can
     * explain to a reader. Anyone wanting "this answer rules that outcome out" is describing a
     * feature that does not exist yet.
     */
    outcome_weights: z.record(id('outcome'), z.number().finite().gt(0).max(100)).optional(),
  })
  .strict();

export type Option = z.infer<typeof optionSchema>;

export const questionSchema = z
  .object({
    id: id('question'),
    type: z.enum(QUESTION_TYPES),
    prompt: z.string().min(1).max(1000),
    /** Empty for free text and scale questions. */
    options: z.array(optionSchema).max(50),
    image_url: z.string().url().max(2048).optional(),
    required: z.boolean(),
    /**
     * Scale questions only, for example 1 to 5.
     *
     * Bounded because the widget renders one tap target per step, and a scale of 1 to 500 is not a
     * scale, it is a number field nobody can use on a phone.
     */
    scale: z
      .object({
        min: z.number().int().min(-1000),
        max: z.number().int().max(1000),
        min_label: z.string().max(60).optional(),
        max_label: z.string().max(60).optional(),
      })
      .optional(),
    /** Free text and form fields. Answers to these go to the PII vault, never into an event. */
    placeholder: z.string().max(200).optional(),
    /**
     * Free text only. Whether the answer wants a line or a paragraph.
     *
     * Optional rather than defaulted, because documents published before this existed do not carry
     * it and the widget has to keep guessing from the format for those. Absent means "the renderer
     * decides", present means the publisher decided.
     */
    multiline: z.boolean().optional(),
  })
  .strict();

export type Question = z.infer<typeof questionSchema>;

/** Personality quiz results. */
export const outcomeSchema = z
  .object({
    id: id('outcome'),
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    image_url: z.string().url().max(2048).optional(),
  })
  .strict();

export type Outcome = z.infer<typeof outcomeSchema>;

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/**
 * How a publisher's widgets look, independent of any one interaction.
 *
 * This is the half of `presentation` that belongs to a brand: colours, corner radius, type and
 * whether the credit is shown. A publisher picks it once and every interaction on that brand
 * inherits it, which is the whole point of splitting it out. `show_results` is deliberately not
 * here, because it is a property of the interaction rather than of the masthead: a survey must say
 * 'never' or it fails its format rules, so a brand level value would either be ignored or would
 * make half the formats unpublishable.
 */
export const themeSchema = z
  .object({
    accent_colour: z.string().regex(HEX_COLOUR),
    text_colour: z.string().regex(HEX_COLOUR),
    background_colour: z.string().regex(HEX_COLOUR),
    corner_radius: z.number().int().min(0).max(40),
    font_family: z.string().max(200).optional(),
    show_brand_credit: z.boolean(),
  })
  .strict();

export type Theme = z.infer<typeof themeSchema>;

/**
 * A theme with every field optional, which is what an org default and a brand override both are.
 *
 * Resolution is per field (see `resolveBrandConfig` in brand.ts), so a brand that only wants a
 * different accent colour sets one key and inherits the rest.
 */
export const themeOverridesSchema = themeSchema.partial().strict();

export type ThemeOverrides = z.infer<typeof themeOverridesSchema>;

/**
 * How the widget looks for one interaction: the brand's theme, plus the one field only an
 * interaction can answer.
 *
 * The widget never inherits styling from the host page, because host page CSS cannot be trusted.
 */
export const presentationSchema = themeSchema
  .extend({
    show_results: z.enum(['after_answer', 'after_completion', 'never']),
  })
  .strict();

export type Presentation = z.infer<typeof presentationSchema>;

/**
 * What a poll looks like before a publisher has themed anything.
 *
 * The values come from the shared design tokens rather than being written out again here, so the
 * defaults a reader sees and the defaults the editor previews cannot drift apart.
 */
/**
 * The bottom layer of theme resolution: what a widget looks like when neither the brand nor the
 * org has said anything.
 */
export const DEFAULT_THEME: Theme = {
  accent_colour: BRAND.accent,
  text_colour: BRAND.ink,
  background_colour: BRAND.surface,
  corner_radius: RADIUS.md,
  show_brand_credit: false,
};

/**
 * The presentation a new interaction starts with.
 *
 * Prefer `defaultPresentation(format)` over this constant. `show_results` here is 'after_answer',
 * which is invalid for a survey or a form, so using it directly means every caller has to remember
 * to override it and one that forgets fails validation at save time.
 */
export const DEFAULT_PRESENTATION: Presentation = {
  ...DEFAULT_THEME,
  show_results: 'after_answer',
};

/** A newsletter list id, as `packages/db` mints them. Shape only; existence is checked at publish. */
const NEWSLETTER_LIST_ID = /^nls_[0-9a-f]{32}$/;

/**
 * The newsletter signup step, as a publisher configures it.
 *
 * It is part of the content rather than a brand setting because it is a decision about this
 * interaction: a publisher offers the newsletter under a quiz about the local election and not
 * under the one about the flower show. The list it feeds is still the brand's, which is why the
 * opt in mode is not here (see `newsletter_opt_in` on the brand record): the same address on the
 * same masthead is the same subscription whichever poll it arrived from, so single or double opt in
 * is a property of the list and would be nonsense to answer twice.
 *
 * `consent_text` absent means inherit: the brand's newsletter wording, then the org's, then the
 * system default, resolved at publish exactly as the theme and the interaction's own notice are.
 * Present means this interaction says something of its own, which is what a one off signup under a
 * campaign needs. It is stored sanitised, like every other consent field, and reaches a reader as
 * segments rather than as markup.
 */
export const newsletterSignupSchema = z
  .object({
    enabled: z.boolean(),
    heading: z.string().max(200).optional(),
    button_label: z.string().max(60).optional(),
    consent_text: z.string().max(CONSENT_STORED_MAX).optional(),
    /**
     * Which of the brand's newsletter lists this signup joins.
     *
     * Absent means the brand's default list, which is exactly what every signup taken before lists
     * existed meant, so no published document has to change and nothing needs backfilling. A
     * publisher with a daily briefing, a sport newsletter and a weekend read names the one this
     * interaction offers.
     *
     * It stays out of the published document on purpose. The list is a routing decision the
     * endpoint makes from the frozen content, not something a reader's browser needs to know or
     * should be able to name, and the document on the CDN is public. See D41.
     */
    list_id: z
      .string()
      .regex(NEWSLETTER_LIST_ID, 'expected a list id like nls_<32 hex>')
      .optional(),
    /**
     * What that list was called when this version was published.
     *
     * Written by publishing and never by an editor: whatever a save leaves here is overwritten the
     * next time the interaction goes live, resolved from the list itself. It is frozen here rather
     * than looked up at signup for two reasons.
     *
     * Why it is frozen: the confirmation email has to name the newsletter somebody is agreeing to,
     * and that email is the single place in the product where a reader is asked to agree to
     * something. Quoting the consent wording covers it when a publisher wrote "Get our sport
     * newsletter" and covers nothing when they wrote "Sign up for updates", which is what most of
     * them write. A name resolved at send time would also mean a publisher renaming a list next
     * year quietly rewrote the record of what a reader was told this year, which is the same
     * argument that freezes the consent wording onto the version rather than reading the brand.
     *
     * Why it is not read on the signup path: that request has a reader waiting on it, and a live
     * read for a value that must not be live would be paying for the wrong answer.
     *
     * Absent means the version was published before this existed, which the email handles.
     */
    list_name: z.string().max(120).optional(),
  })
  .strict();

export type NewsletterSignup = z.infer<typeof newsletterSignupSchema>;

/**
 * The editable body of an interaction, independent of who owns it or whether it is published.
 */
export const interactionContentSchema = z
  .object({
    format: z.enum(FORMATS),
    title: z.string().min(1).max(300),
    /** Shown above the first question. Optional for a single question poll. */
    introduction: z.string().max(2000).optional(),
    questions: z.array(questionSchema).min(1).max(100),
    outcomes: z.array(outcomeSchema).max(30).optional(),
    completion_message: z.string().max(2000).optional(),
    /**
     * This interaction's own look, or absent to take the brand's.
     *
     * Optional is what makes a brand theme mean anything: if every interaction carried a full
     * presentation, the brand's would never be used and a publisher would be picking their colours
     * again on every poll. Absent means "whatever the brand says", resolved at publish time and
     * frozen into the published version (see `resolvePresentation`), so the published document
     * stays self contained and the widget still reads one static file.
     *
     * Present means this interaction was themed on its own and the brand does not apply to it.
     */
    presentation: presentationSchema.optional(),
    /**
     * Whether this interaction offers the newsletter when a reader has finished, and in what words.
     *
     * Optional, and absent means no step, which is what every interaction published before this
     * existed carries. Publishing resolves the wording and freezes it, and leaves the block out of
     * the document altogether when the step is switched off, so the widget's "absent means no step"
     * path stays the only one it has.
     */
    signup: newsletterSignupSchema.optional(),
  })
  .strict();

export type InteractionContent = z.infer<typeof interactionContentSchema>;

/**
 * Whether a human wrote this or a model drafted it.
 *
 * AI drafted and human drafted interactions are the same object with a different flag, which is
 * what keeps generation from becoming a parallel content pipeline.
 */
export const provenanceSchema = z
  .object({
    source: z.enum(['human', 'ai_draft']),
    model: z.string().max(100).optional(),
    /**
     * The publisher's article a draft was written from, when it was written from one.
     *
     * Provenance is what an editor answers for later, and "a model wrote this" without saying what
     * it read is half an answer: the questions in front of them assert things, and the only way to
     * check an assertion is to go back to where it came from. Optional because a draft from a typed
     * brief has no article, and because every record written before this existed has no field.
     *
     * It is always a URL on the brand's own domains, since that is the only kind the drafting route
     * will fetch. See D38. The request body calls it `article_url`, because that is what a caller
     * hands over; this is where the draft came from, which is a different question.
     */
    source_url: z.string().url().max(2048).optional(),
    /** AI drafted content cannot be published until a person has approved it. */
    approved_by: id('user').optional(),
    approved_at: isoTimestamp.optional(),
    /**
     * A fingerprint of the content as it stood when a person approved it.
     *
     * Approval has to be bound to what was approved. Without this, approve, edit, publish leaves
     * `approved_by` set on content nobody read, and the record would claim a person signed off
     * something they never saw. Publishing recomputes the fingerprint and refuses if it has moved.
     *
     * Optional because interactions written before this existed carry no fingerprint. Absent is
     * not treated as "fine", it is treated as stale: see the publish route.
     */
    approved_content_hash: z
      .string()
      .regex(/^[0-9a-f]{64}$/, 'expected a sha-256 hex digest')
      .optional(),
  })
  .strict();

export type Provenance = z.infer<typeof provenanceSchema>;
